// highlight-highlighter.js - Highlight/mark text with yellow background for ==text== syntax
// Similar to Obsidian's highlight feature, with cursor-aware marker hiding

import { RangeSetBuilder } from "@codemirror/state";
import { Decoration, ViewPlugin } from "@codemirror/view";

// Decoration for the content (yellow background)
const highlightContent = Decoration.mark({
    class: "cm-highlight-content"
});

// Decoration for the == markers (hidden by default)
const highlightMarkerHidden = Decoration.mark({
    class: "cm-highlight-marker cm-highlight-marker-hidden"
});

// Decoration for the == markers (visible when cursor inside)
const highlightMarkerVisible = Decoration.mark({
    class: "cm-highlight-marker"
});

function findHighlights(view) {
    const doc = view.state.doc;
    const text = doc.toString();
    const cursorPos = view.state.selection.main.head;

    // Collect all decorations
    const decorations = [];

    // Find all ==text== patterns (double equals, non-greedy, single line)
    const regex = /==([^=\n]+)==/g;
    let match;

    while ((match = regex.exec(text)) !== null) {
        const fullStart = match.index;
        const fullEnd = match.index + match[0].length;
        const contentStart = fullStart + 2;
        const contentEnd = fullEnd - 2;

        // Check if cursor is inside this highlight (including markers)
        const cursorInside = cursorPos >= fullStart && cursorPos <= fullEnd;
        const markerDeco = cursorInside ? highlightMarkerVisible : highlightMarkerHidden;

        // Opening ==
        decorations.push({ from: fullStart, to: contentStart, deco: markerDeco });
        // Content with yellow background
        decorations.push({ from: contentStart, to: contentEnd, deco: highlightContent });
        // Closing ==
        decorations.push({ from: contentEnd, to: fullEnd, deco: markerDeco });
    }

    // Sort by position and build RangeSet
    decorations.sort((a, b) => a.from - b.from);

    const builder = new RangeSetBuilder();
    for (const { from, to, deco } of decorations) {
        builder.add(from, to, deco);
    }

    return builder.finish();
}

export const highlightHighlighter = ViewPlugin.fromClass(class {
    constructor(view) {
        this.decorations = findHighlights(view);
    }

    update(update) {
        // Update on doc changes, viewport changes, or selection changes (cursor moved)
        if (update.docChanged || update.viewportChanged || update.selectionSet) {
            this.decorations = findHighlights(update.view);
        }
    }
}, {
    decorations: v => v.decorations
});
